import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { usePricingMutations } from '@/hooks/usePricing'
import type { PricingCategory } from '@/types/pricing'

const BASE_CATEGORIES = [
  { value: '', label: 'Any (no filter)' },
  { value: 'piston_single', label: 'Piston Single' },
  { value: 'piston_multi', label: 'Piston Multi' },
  { value: 'turboprop', label: 'Turboprop' },
  { value: 'light_jet', label: 'Light Jet' },
  { value: 'midsize_jet', label: 'Midsize Jet' },
  { value: 'super_midsize_jet', label: 'Super Midsize Jet' },
  { value: 'heavy_jet', label: 'Heavy Jet' },
  { value: 'ultra_long_range', label: 'Ultra Long Range' },
  { value: 'helicopter', label: 'Helicopter' },
]

export function CategoryEditDialog({
  open,
  onOpenChange,
  category,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  category: PricingCategory | null
}) {
  const mutations = usePricingMutations()
  const isEdit = !!category

  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [sortOrder, setSortOrder] = useState(0)
  const [matchBaseCategory, setMatchBaseCategory] = useState('')
  const [matchEngineCount, setMatchEngineCount] = useState('')
  const [matchMinWeight, setMatchMinWeight] = useState('')
  const [matchMaxWeight, setMatchMaxWeight] = useState('')
  const [matchFuelType, setMatchFuelType] = useState('')

  useEffect(() => {
    if (category) {
      setCode(category.code)
      setName(category.name)
      setSortOrder(category.sortOrder)
      setMatchBaseCategory(category.matchBaseCategory || '')
      setMatchEngineCount(category.matchEngineCount?.toString() ?? '')
      setMatchMinWeight(category.matchMinWeight?.toString() ?? '')
      setMatchMaxWeight(category.matchMaxWeight?.toString() ?? '')
      setMatchFuelType(category.matchFuelType || '')
    } else {
      setCode('')
      setName('')
      setSortOrder(0)
      setMatchBaseCategory('')
      setMatchEngineCount('')
      setMatchMinWeight('')
      setMatchMaxWeight('')
      setMatchFuelType('')
    }
  }, [category, open])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const input = {
      code: code.toLowerCase().replace(/\s+/g, '_'),
      name,
      sortOrder,
      matchBaseCategory: matchBaseCategory || undefined,
      matchEngineCount: matchEngineCount ? parseInt(matchEngineCount) : undefined,
      matchMinWeight: matchMinWeight ? parseInt(matchMinWeight) : undefined,
      matchMaxWeight: matchMaxWeight ? parseInt(matchMaxWeight) : undefined,
      matchFuelType: matchFuelType || undefined,
    }

    if (isEdit) {
      const dbUpdate: Record<string, unknown> = {
        code: input.code,
        name: input.name,
        sort_order: input.sortOrder,
        match_base_category: input.matchBaseCategory || null,
        match_engine_count: input.matchEngineCount ?? null,
        match_min_weight: input.matchMinWeight ?? null,
        match_max_weight: input.matchMaxWeight ?? null,
        match_fuel_type: input.matchFuelType || null,
      }
      mutations.updateCategory.mutate(
        { id: category!.id, data: dbUpdate },
        { onSuccess: () => onOpenChange(false) }
      )
    } else {
      mutations.createCategory.mutate(input, {
        onSuccess: () => onOpenChange(false),
      })
    }
  }

  const isPending = mutations.createCategory.isPending || mutations.updateCategory.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEdit ? 'Edit Category' : 'Add Pricing Category'}</DialogTitle>
            <DialogDescription>
              Define classification rules to auto-assign aircraft to this pricing tier.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Light Jet"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label>Code</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="e.g., light_jet"
                  required
                  className="font-mono"
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Sort Order (lower = checked first)</Label>
              <Input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Classification Rules</p>
              <p className="text-xs text-muted-foreground mb-4">
                All specified rules must match (AND logic). Leave empty for "any".
              </p>

              <div className="grid gap-3">
                <div className="grid gap-2">
                  <Label>Base Category</Label>
                  <Select value={matchBaseCategory} onValueChange={setMatchBaseCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      {BASE_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value || '_any'} value={cat.value || '_any'}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Min Weight (lbs)</Label>
                    <Input
                      type="number"
                      value={matchMinWeight}
                      onChange={(e) => setMatchMinWeight(e.target.value)}
                      placeholder="Any"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Max Weight (lbs)</Label>
                    <Input
                      type="number"
                      value={matchMaxWeight}
                      onChange={(e) => setMatchMaxWeight(e.target.value)}
                      placeholder="Any"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Engine Count</Label>
                    <Input
                      type="number"
                      value={matchEngineCount}
                      onChange={(e) => setMatchEngineCount(e.target.value)}
                      placeholder="Any"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Fuel Type</Label>
                    <Select
                      value={matchFuelType || '_any'}
                      onValueChange={(v) => setMatchFuelType(v === '_any' ? '' : v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Any" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_any">Any</SelectItem>
                        <SelectItem value="jet_a">Jet A</SelectItem>
                        <SelectItem value="avgas_100ll">AvGas 100LL</SelectItem>
                        <SelectItem value="avgas_ul94">AvGas UL94</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !name || !code}>
              {isPending ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Category'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
